import {animate, style, transition, trigger} from '@angular/animations';
import {Match, MatchWithKey} from './models';

export function getColorText(color: 'red' | 'blue'): string {
  return color === 'red' ? $localize`red` : $localize`blue`;
}

export function getBreakText(breakType?: string): string {
  switch (breakType) {
    case 'Break':
      return $localize`Break`;
    case 'Lunch':
      return $localize`Lunch`;
    case 'End of day':
      return $localize`End of day`;
    case 'Alliance selection':
      return $localize`Alliance selection`;
    case 'Awards break':
      return $localize`Awards break`;
    case 'Opening ceremonies':
      return $localize`Opening ceremonies`;
  }

  return breakType ?? '';
}

export function getAnnouncementTitleText(title: string): string {
  switch (title) {
    case 'Event announcement':
      return $localize`Event announcement`;
    case 'Alliance selection':
      return $localize`Alliance selection`;
    case 'Match replay':
      return $localize`Match replay`;
  }

  return title;
}

export function getMatchDescriptionText(match: MatchWithKey): string {
  if (!match.round) return '';

  const round = match.round;
  const matchNumber = getMatchNumberFromKey(match.key);

  if (!match.advancement) {
    return $localize`Round ${round}- Match ${matchNumber}`;
  }

  if (match.advancement.loser) {
    return $localize`Round ${round}- Upper bracket- Match ${matchNumber}`;
  }

  return $localize`Round ${round}- Lower bracket- Match ${matchNumber}`;
}

export function getLocalTimeString(time?: number) {
  if (!time) return '';

  const tzoffset = new Date(time).getTimezoneOffset() * 60000;
  return new Date(time - tzoffset).toISOString().slice(0, 16);
}

export function isDemo(eventKey: string): boolean {
  return eventKey.startsWith('demo');
}

export function isPlayoff(matchKey: string) {
  return ['de', 'f'].includes(getMatchTypeFromMatchKey(matchKey));
}

export function isReplay(matchKey: string) {
  return matchKey.includes('r');
}

export function replayOf(matchKey: string) {
  return matchKey.split('r')[0];
}

export function matchKeyToText(key: string, eventKey?: string) {
  const type = getMatchTypeFromMatchKey(key);
  let suffix = '';
  if (isReplay(key)) {
    const replay = +key.split('r')[1];
    key = replayOf(key);
    if (replay > 1) {
      suffix = ` ` + $localize`Replay` + ` ${replay}`;
    } else {
      suffix = ' ' + $localize`Replay`;
    }
  }

  if (type === 'p' || type == 'qm') {
    return `${matchTypeToText(type)} ${getMatchNumberFromKey(key)}${suffix}`;
  }

  const setMatch = key.substring(type.length);
  const splitMatch = setMatch.split('m');

  if (splitMatch.length === 2) {
    if (type === 'f') {
      return `${matchTypeToText(type, eventKey)} ${splitMatch[1]}${suffix}`;
    }

    return `${matchTypeToText(type, eventKey)} ${splitMatch[0]}-${splitMatch[1]}${suffix}`;
  }

  return `${matchTypeToText(type, eventKey)} ${setMatch}${suffix}`;
}

const EINSTEIN_EVENT_CODE = 'cmptx';

export function matchTypeToText(type: string, eventKey?: string) {
  switch (type) {
    case 'p':
      return $localize`Practice`;
    case 'qm':
      return $localize`Qualification`;
    case 'de':
      return eventKey?.endsWith(EINSTEIN_EVENT_CODE) ? 'Einstein' : $localize`Playoff`;
    case 'f':
      return eventKey?.endsWith(EINSTEIN_EVENT_CODE) ? 'Einstein Final' : $localize`Final`;
    default:
      return '';
  }
}

export function getStatusText(state: string) {
  switch (state) {
    case 'queued':
      return $localize`Now queuing`;
    case 'on_deck':
      return $localize`On deck`;
    case 'complete':
      return $localize`On field`;
    case 'waiting':
      return $localize`Queuing soon`;
    default:
      return state;
  }
}

export function getMatchStatusText(match: Match, now?: number, matchDuration?: number) {
  if(match.status === 'complete') {
    if(match.timestamps?.committed) {
      return $localize`Complete`;
    }
    if(match.timestamps?.started) {
      if(now && matchDuration && now > match.timestamps.started + matchDuration) {
        return $localize`Awaiting scores`;
      }
      return $localize`Running`;
    }
  }

  return getStatusText(match.status);
}

export function getMatchTypeFromMatchKey(key: string) {
  if (!key) return '';

  const one = key.substring(0, 1);
  const two = key.substring(0, 2);
  if (matchTypeOrder.includes(two)) {
    return two;
  }
  if (matchTypeOrder.includes(one)) {
    return one;
  }
  return '';
}

export function getMatchNumberFromKey(key: string) {
  const type = getMatchTypeFromMatchKey(key);

  if (isReplay(key)) {
    key = replayOf(key);
  }

  if (type === 'qm' || type === 'de') {
    return +key.substring(2);
  }
  if (type === 'p') {
    return +key.substring(1);
  }

  const subStart = type === 'f' ? 1 : 2;
  const splitMatchNum = key.substring(subStart).split('m');

  if (splitMatchNum.length === 1) {
    splitMatchNum[1] = '1';
  }

  return +splitMatchNum[0] + +splitMatchNum[1] * 100;
}

export const fadeAnimation = trigger('fade', [
  transition(':enter', [style({ opacity: 0 }), animate('250ms', style({ opacity: 1 }))]),
  transition(':leave', [animate('250ms', style({ opacity: 0 }))]),
]);

export const fadeInAnimation = trigger('fadeIn', [
  transition(':enter', [style({ opacity: 0 }), animate('250ms', style({ opacity: 1 }))]),
]);

export function getShortUrlEventKey(eventKey: string): string {
  if (isDemo(eventKey)) return eventKey;

  const year = eventKey.substring(0, 4);
  if (year === new Date().getUTCFullYear().toString()) {
    return eventKey.substring(4);
  }

  return eventKey;
}

export const matchTypeOrder = ['p', 'qm', 'de', 'f'];
export function getSortedMatches(matchesObject: {[matchKey: string]: Match}): MatchWithKey[] {
  const result: MatchWithKey[] = [];
  const replays = [];
  for (let match in matchesObject) {
    if (matchesObject[match].afterMatchId) {
      replays.push({ key: match, ...matchesObject[match], label: '' });
    } else {
      result.push({ key: match, ...matchesObject[match], label: '' });
    }
  }

  result.sort((a, b) => {
    const typeA = getMatchTypeFromMatchKey(a.key);
    const typeB = getMatchTypeFromMatchKey(b.key);

    if (typeA !== typeB) {
      return matchTypeOrder.indexOf(typeA) - matchTypeOrder.indexOf(typeB);
    }

    return getMatchNumberFromKey(a.key) - getMatchNumberFromKey(b.key);
  });

  let failsafe = 0;
  while (replays.length && failsafe < 100) {
    failsafe++;
    for (let i = 0; i < replays.length; i++) {
      const replay = replays[i];
      const insertIndex = result.findIndex((match) => match.key === replay.afterMatchId);
      if (insertIndex >= 0) {
        result.splice(insertIndex + 1, 0, replay);
        replays.splice(i, 1);
        i--;
      } else {
        if (!replays.find((match) => match.key === replay.afterMatchId)) {
          console.warn('cannot insert replay after match ' + replay.afterMatchId);
          result.push(replay);
          replays.splice(i, 1);
          i--;
        }
      }
    }
  }

  return result;
}

// ALSO UPDATE api_utils.ts.
export const MATCH_DURATION = (2.5 + 0.5) * 60 * 1000;
const LONG_BREAK_THRESHOLD_MINUTES = 10;
export function addDynamicQueueTime(
  matches: MatchWithKey[],
  practiceCycleTimeSeconds: number,
  qualificationCycleTimeSeconds: number,
  playoffCycleTimeSeconds: number,
  queueSingleMatch: boolean,
  queueExtraMatch: boolean,
  now: number,
  fieldTimer: number|null,
  autoQueueEnabled: boolean,
) {
  if (!matches.length) return;

  function getCycleTime(matchKey: string) {
    switch (getMatchTypeFromMatchKey(matchKey)) {
      case 'p':
        return practiceCycleTimeSeconds * 1000;
      case 'de':
      case 'f':
        return playoffCycleTimeSeconds * 1000;
      case 'qm':
      default:
        return qualificationCycleTimeSeconds * 1000;
    }
  }

  const firstMatchQueueDuration = (queueExtraMatch ? 20 : 15) * 60 * 1000;

  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const previousMatch = matches[i - 1];
    const nextMatch = matches[i + 1];
    const nextNextMatch = matches[i + 2];
    const nextNextNextMatch = matches[i + 3];
    const prepTime = getCycleTime(match.key) - MATCH_DURATION;

    if (match.queueTime) {
      match.queueTimeLocal = getLocalTimeString(match.queueTime);
    }

    if (getMatchTypeFromMatchKey(match.key) === 'de' && getMatchTypeFromMatchKey(previousMatch?.key) !== 'de' && !match.queueTime && previousMatch?.breakAfter && match.status === 'waiting') {
      continue;
    }

    if(match.timeInterval && previousMatch?.estimatedOnFieldTime) {
      if(match.status === 'complete' && match.timestamps?.complete) {
        match.estimatedOnFieldTime = match.timestamps.complete;
        match.onFieldMsAgo = now - match.timestamps.complete;
      } else {
        match.estimatedOnFieldTime = previousMatch.estimatedOnFieldTime + (match.timeInterval * 1000);
      }
      match.estimatedStartTime = match.timestamps?.started || (match.estimatedOnFieldTime + (7 * 60 * 1000));
      match.estimatedOnDeckTime = match.estimatedOnFieldTime - (13 * 60 * 1000);
      match.estimatedQueueTime = match.estimatedOnDeckTime;
      continue;
    }

    if (match.status !== 'waiting' && match.timestamps?.queued) {
      match.estimatedQueueTime = match.timestamps.queued;
    } else {
      if (previousMatch?.pseudoBreakAfter || previousMatch?.breakDurationMinutes) {
        if (previousMatch?.estimatedOnFieldTime) {
          match.estimatedQueueTime = previousMatch.estimatedOnFieldTime;

          if (
            previousMatch?.breakDurationMinutes &&
            previousMatch.breakDurationMinutes >= LONG_BREAK_THRESHOLD_MINUTES
          ) {
            match.estimatedQueueTime += getCycleTime(match.key);

            if(previousMatch?.timestamps?.started) {
              match.estimatedQueueTime = previousMatch.timestamps.started + MATCH_DURATION;
            } else {
              if(autoQueueEnabled && previousMatch?.status === 'complete' && (match.estimatedQueueTime - now < MATCH_DURATION)) {
                match.estimatedQueueTime = now + MATCH_DURATION;
              }
            }
          }
        }
      } else {
        if (queueSingleMatch) {
          if (previousMatch?.estimatedOnFieldTime) {
            match.estimatedQueueTime = previousMatch.estimatedOnFieldTime;
          }
        } else {
          if (queueExtraMatch) {
            if (previousMatch?.estimatedQueueTime) {
              match.estimatedQueueTime = previousMatch.estimatedQueueTime + getCycleTime(match.key);
            }
          } else {
            if (previousMatch?.estimatedOnDeckTime) {
              match.estimatedQueueTime = previousMatch.estimatedOnDeckTime;
            }
          }
        }
      }

      if (match.queueTime) {
        if (match.estimatedQueueTime && previousMatch?.estimatedQueueTime) {
          if (match.queueTime >= match.estimatedQueueTime) {
            match.estimatedQueueTime = match.queueTime;
          } else {
            if (previousMatch?.status !== 'complete') {
              match.breakOverrun = true;
            }
          }
        } else {
          match.estimatedQueueTime = match.queueTime;
        }
      }

      if (match.queueBeforeOpeningCeremonies && previousMatch?.queueBeforeOpeningCeremonies && previousMatch.estimatedQueueTime) {
        match.estimatedQueueTime = previousMatch.estimatedQueueTime;
      }

      if (match.estimatedQueueTime && match.estimatedQueueTime < now) {
        match.estimatedQueueTime = now;
      }
    }

    if (['on_deck', 'complete'].includes(match.status) && match.timestamps?.on_deck) {
      match.estimatedOnDeckTime = match.timestamps.on_deck;
    } else {
      if (match.queueTime && !previousMatch?.queueBeforeOpeningCeremonies) {
        match.estimatedOnDeckTime = match.estimatedQueueTime;
      } else {
        if (queueSingleMatch) {
          match.estimatedOnDeckTime = match.estimatedQueueTime;
        } else {
          if (previousMatch?.breakDurationMinutes && match.estimatedQueueTime) {
            match.estimatedOnDeckTime =
              match.estimatedQueueTime +
              (previousMatch.breakDurationMinutes >= LONG_BREAK_THRESHOLD_MINUTES ? 0 : getCycleTime(match.key));
            
            if(previousMatch?.timestamps?.started) {
              match.estimatedOnDeckTime = previousMatch.timestamps.started + MATCH_DURATION;
            }
          } else {
            if (previousMatch?.estimatedOnFieldTime) {
              match.estimatedOnDeckTime = previousMatch.estimatedOnFieldTime;
            }
          }
        }
      }

      if (match.estimatedOnDeckTime && match.estimatedOnDeckTime < now) {
        match.estimatedOnDeckTime = now;
      }
    }

    if (match.status === 'complete' && match.timestamps?.complete) {
      match.estimatedOnFieldTime = match.timestamps.complete;
      match.onFieldMsAgo = now - match.timestamps.complete;
    } else {
      if(previousMatch?.breakDurationMinutes && match.status === 'on_deck' && fieldTimer) {
        match.estimatedOnFieldTime = fieldTimer;
      } else {
        if (match.queueTime && !previousMatch?.queueBeforeOpeningCeremonies) {
          if(queueExtraMatch) {
            if (match.queueBeforeOpeningCeremonies && nextMatch?.queueBeforeOpeningCeremonies && nextNextMatch?.queueBeforeOpeningCeremonies && nextNextNextMatch?.queueTime) {
              match.estimatedOnFieldTime = nextNextNextMatch.queueTime;
            } else {
              match.estimatedOnFieldTime = match.queueTime + firstMatchQueueDuration - prepTime;
            }
          } else {
            if (match.queueBeforeOpeningCeremonies && nextMatch?.queueBeforeOpeningCeremonies && nextNextMatch?.queueTime) {
              match.estimatedOnFieldTime = nextNextMatch.queueTime;
            } else {
              match.estimatedOnFieldTime = match.queueTime + firstMatchQueueDuration - prepTime;
            }
          }
        } else {
          if (match.estimatedOnDeckTime) {
            if (previousMatch?.breakDurationMinutes) {
              match.estimatedOnFieldTime = match.estimatedOnDeckTime + previousMatch.breakDurationMinutes * 60 * 1000;
            } else {
              match.estimatedOnFieldTime = match.estimatedOnDeckTime + getCycleTime(match.key);
            }
          }

          if(previousMatch?.timestamps?.started) {
            match.estimatedOnFieldTime = previousMatch.timestamps.started + MATCH_DURATION + (previousMatch?.breakDurationMinutes ? previousMatch.breakDurationMinutes * 60 * 1000 : 0);
          }
        }
      }

      if (match.estimatedOnFieldTime && match.estimatedOnFieldTime < now) {
        match.estimatedOnFieldTime = now;
      }
    }

    if (match.estimatedOnFieldTime) {
      const isTimedBreak = !!previousMatch?.breakDurationMinutes;
      match.estimatedStartTime = match.estimatedOnFieldTime + (isTimedBreak ? (1000 * 60 * 3) : prepTime);

      if(autoQueueEnabled && !match.timestamps?.started && match.estimatedStartTime < now) {
        match.estimatedStartTime = now;
      }
    }

    if (match.timestamps?.started) {
      match.estimatedStartTime = match.timestamps.started;
    }
  }
}

export function addMatchLabels(matches: MatchWithKey[], eventKey: string) {
  const playoffMatches = matches.filter((m) => isPlayoff(m.key));

  const completeMatches = matches.filter((m) => m.status === 'complete');
  if (completeMatches && completeMatches.length) {
    completeMatches[completeMatches.length - 1].current = true;
  }

  for (const match of matches) {
    match.label = matchKeyToText(match.key, eventKey);

    const description = getMatchDescriptionText(match);
    if (description) {
      match.description = description;
    }

    if (isPlayoff(match.key)) {
      let keyToSearchFor = match.key;
      if (getMatchTypeFromMatchKey(match.key) === 'f') {
        keyToSearchFor = 'f1';
      }

      if (match.redAlliance === undefined) {
        let winner = true;
        let sourceMatch = playoffMatches.find(
          (m) => m.advancement?.winner.match === keyToSearchFor && m.advancement?.winner.color === 'red'
        );
        if (!sourceMatch) {
          sourceMatch = playoffMatches.find(
            (m) => m.advancement?.loser?.match === keyToSearchFor && m.advancement?.loser?.color === 'red'
          );
          winner = false;
        }

        if (sourceMatch) {
          const matchNumber = getMatchNumberFromKey(sourceMatch.key);
          if (winner) {
            match.redUndecidedLabel = $localize`Winner of ${sourceMatch.label}`;
            match.redUndecidedLabelShort = $localize`:Single letter abbreviation indicating the winner of the given match number:W${matchNumber}`;
          } else {
            match.redUndecidedLabel = $localize`Loser of ${sourceMatch.label}`;
            match.redUndecidedLabelShort = $localize`:Single letter abbreviation indicating the loser of the given match number:L${matchNumber}`;
          }
        } else {
          match.redUndecidedLabel = $localize`Red alliance`;
        }
      }
      if (match.blueAlliance === undefined) {
        let winner = true;
        let sourceMatch = playoffMatches.find(
          (m) => m.advancement?.winner.match === keyToSearchFor && m.advancement?.winner.color === 'blue'
        );
        if (!sourceMatch) {
          sourceMatch = playoffMatches.find(
            (m) => m.advancement?.loser?.match === keyToSearchFor && m.advancement?.loser?.color === 'blue'
          );
          winner = false;
        }

        if (sourceMatch) {
          const matchNumber = getMatchNumberFromKey(sourceMatch.key);
          if (winner) {
            match.blueUndecidedLabel = $localize`Winner of ${sourceMatch.label}`;
            match.blueUndecidedLabelShort = $localize`:Single letter abbreviation indicating the winner of the given match number:W${matchNumber}`;
          } else {
            match.blueUndecidedLabel = $localize`Loser of ${sourceMatch.label}`;
            match.blueUndecidedLabelShort = $localize`:Single letter abbreviation indicating the loser of the given match number:L${matchNumber}`;
          }
        } else {
          match.blueUndecidedLabel = $localize`Blue alliance`;
        }
      }
    }
  }
}

export function getMondayBeforeTimestamp(timestamp: number) {
  const date = new Date(timestamp);
  let dayDiff = 1 - date.getDay();
  let hourDiff = 5 - date.getHours();
  if (dayDiff > 0) {
    dayDiff = -1;
  }

  return timestamp + (dayDiff * 1000 * 60 * 60 * 24) + (hourDiff * 1000 * 60 * 60);
}
